/**
 * This mixin holds common signage screens component code for periodically fetching the Screen and Showings
 * data and determining what showings to display as now playing or upcoming.
 *
 * This mixin requires that the component configures the `screen` to be fetched in its `models` object,
 * including its `todaysShowings` data. For example:
 * ```js
 *  models: {
 *    screen: {
 *      fields: ['id', 'name', ...],
 *      todaysShowings: {
 *        ...
 *      },
 *      variables () {
 *        return {
 *          id: ...
 *        }
 *      },
 *      immediate: true
 *    }
 *  }
 * ```
 */
export default {
  configuration: ['currentSite'],
  // Begin periodically fetching the screen and showings data
  async mounted () {
    this.intervalId = setInterval(() => {
      this.screen.fetch(true)
    }, 5 * 60 * 1000) // every 5 minutes

    await this.screen.fetch()
  },
  unmounted () {
    if (this.intervalId) clearInterval(this.intervalId)
  },
  computed: {
    todaysNowPlayingAndUpcomingShowings () {
      if (_.isEmpty(this.screen?.todaysShowings?.data)) return []

      return _.filter(this.screen.todaysShowings.data, (showing) => {
        return showing.withinSignageThreshold || showing.siteTime > this.currentSite.siteTime || showing.siteEndTime > this.currentSite.siteTime
      })
    },
    currentShowing () {
      if (!this.todaysNowPlayingAndUpcomingShowings.length) return null

      const currentShowing = _.find(this.todaysNowPlayingAndUpcomingShowings, { withinSignageThreshold: true })
      if (currentShowing) return currentShowing

      // If we got this far, it means there's no showing within the configured signage threshold
      // so we'll show the next showing or none at all if there are no more showings today.
      const nextShowingStartingOrEndingToday = _.find(this.todaysNowPlayingAndUpcomingShowings, (showing) => {
        return showing.siteTime > this.currentSite.siteTime || showing.siteEndTime > this.currentSite.siteTime
      })
      return nextShowingStartingOrEndingToday
    },
    showingStatus () {
      const currentTime = this.currentSite.siteTime
      if (currentTime > this.currentShowing.siteEndTime) {
        // Showing has ended
        return ''
      }

      const startTime = this.currentShowing.siteTime
      const diffInMilliseconds = Math.abs(currentTime - startTime)
      const diffInMinutes = diffInMilliseconds / 1000 / 60

      if (currentTime > startTime) {
        // Showing has started and not yet ended
        return 'Now Playing'
      } else if (diffInMinutes <= 30) {
        // From 30 mins before the movie starts up until the start time
        return 'Now Seating'
      } else {
        // Not yet starting within the next 30 minutes
        return 'Next Showing'
      }
    },
    showingStatusColor () {
      const currentTime = this.currentSite.siteTime
      if (currentTime > this.currentShowing.siteEndTime) {
        // Showing has ended
        return ''
      }

      const startTime = this.currentShowing.siteTime
      const diffInMilliseconds = Math.abs(currentTime - startTime)
      const diffInMinutes = diffInMilliseconds / 1000 / 60

      if (currentTime > startTime) {
        // Showing has started and not yet ended
        return this.currentSite.positiveColor
      } else if (diffInMinutes <= 30) {
        // From 30 mins before the movie starts up until the start time
        return this.currentSite.positiveColor
      } else {
        // Not yet starting within the next 30 minutes
        return this.currentSite.primaryColor
      }
    }
  }
}
